import { useState } from "react";
import { Heart, Share2, Trash2, Eye, EyeOff, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fileUrl, shareLink } from "../lib/api";
import { ImageUpload } from "./ImageUpload";
import { Dialog, DialogContent } from "./ui/dialog";

const PhotoCard = ({ p, athlete, canEdit, onChange, onOpen }) => {
  const { user, isAdmin } = useAuth();
  const liked = p.likes.includes(user.user_id);
  const like = async () => { const { data } = await api.post(`/photos/${p.id}/like`); onChange(data); };
  const share = async () => {
    const r = await shareLink(`${window.location.origin}/app/atletas/${athlete.user_id}`, `Foto de ${athlete.name} - Circuito Altax`);
    if (r === "copied") toast.success("Link do perfil copiado!");
  };
  const toggle = async () => { const { data } = await api.post(`/photos/${p.id}/visibility`); onChange(data); toast.success(data.hidden ? "Foto ocultada" : "Foto visível"); };
  const del = async () => { if (!window.confirm("Excluir foto?")) return; await api.delete(`/photos/${p.id}`); onChange(null, p.id); };
  return (
    <div data-testid={`photo-${p.id}`} className="card-x min-w-0 overflow-hidden">
      <button onClick={() => onOpen(p)} className="relative block aspect-square w-full bg-[#0B0E14]" data-testid={`photo-open-${p.id}`}>
        <img src={fileUrl(p.path)} alt="" className={`h-full w-full object-cover ${p.hidden ? "opacity-40" : ""}`} />
        {p.hidden && <span data-testid={`photo-hidden-badge-${p.id}`} className="absolute left-1.5 top-1.5 rounded-full bg-yellow-400 px-2 py-0.5 text-[9px] font-bold uppercase text-black">Oculta</span>}
      </button>
      <div className="flex flex-wrap items-center justify-between gap-0.5 p-1.5">
        <button data-testid={`photo-like-${p.id}`} onClick={like} className={`flex items-center gap-1 rounded-full px-1.5 py-1 text-xs ${liked ? "text-red-400" : "text-[#94A3B8] hover:text-white"}`}>
          <Heart className={`h-4 w-4 ${liked ? "fill-red-400" : ""}`} />{p.likes.length}
        </button>
        <div className="flex items-center">
          <button data-testid={`photo-share-${p.id}`} onClick={share} className="rounded-full p-1.5 text-[#94A3B8] hover:text-white"><Share2 className="h-4 w-4" /></button>
          {isAdmin && <button data-testid={`photo-visibility-${p.id}`} title={p.hidden ? "Mostrar foto" : "Ocultar foto"} onClick={toggle} className={`rounded-full p-1.5 ${p.hidden ? "text-yellow-300" : "text-[#94A3B8] hover:text-white"}`}>{p.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>}
          {canEdit && <button data-testid={`photo-delete-${p.id}`} onClick={del} className="rounded-full p-1.5 text-[#94A3B8] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>
    </div>
  );
};

export const ProfilePhotos = ({ athlete }) => {
  const { user, isAdmin } = useAuth();
  const [photos, setPhotos] = useState(athlete.photos || []);
  const [open, setOpen] = useState(null);
  const canEdit = isAdmin || user.user_id === athlete.user_id;
  const add = async (path) => {
    if (!path) return;
    try { const { data } = await api.post(`/athletes/${athlete.user_id}/photos`, { path }); setPhotos([...photos, data]); } catch (e) { toast.error(errMsg(e)); }
  };
  const change = (np, removedId) => setPhotos(np ? photos.map((x) => (x.id === np.id ? np : x)) : photos.filter((x) => x.id !== removedId));
  return (
    <div className="mt-5" data-testid="profile-photos">
      <div className="mb-3 flex items-center justify-between">
        <div className="h-display text-2xl">Fotos <span className="text-base text-[#64748B]">({photos.length}/3)</span></div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {photos.map((p) => <PhotoCard key={p.id} p={p} athlete={athlete} canEdit={canEdit} onChange={change} onOpen={setOpen} />)}
        {canEdit && photos.length < 3 && (
          <div className="card-x flex aspect-square min-w-0 items-center justify-center border-dashed">
            <ImageUpload value={null} onChange={add} testid="photo-add-upload" label="Adicionar" />
          </div>
        )}
        {!canEdit && photos.length === 0 && <p className="col-span-3 flex items-center gap-2 text-sm text-[#64748B]"><ImagePlus className="h-4 w-4" />Nenhuma foto publicada.</p>}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="border-[#232B3E] bg-[#0B0E14] p-2 sm:max-w-2xl">
          {open && <img src={fileUrl(open.path)} alt="" className="max-h-[80vh] w-full rounded-xl object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
